import React, { useEffect, useState, useMemo } from 'react';
import {
  View, Text, ScrollView, StyleSheet,
  ActivityIndicator, TouchableOpacity, useWindowDimensions,
} from 'react-native';
import {
  collection, query, where, orderBy,
  onSnapshot, getDocs, limit, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { OrderModel } from '../model/orderModel';

// ── Types ─────────────────────────────────────────────────────────────────────
interface DailyDashboardProps { branchId: string }
interface DPMap { [uid: string]: string }
interface RecentUser {
  id: string; phone: string; username?: string | null;
  walletBalance: number; notactive: boolean; createdTimestamp: any;
}

// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  panel: '#0A1712', primary: '#21808D', teal: '#4ECDC4', green: '#21C98E',
  red: '#ef4444', amber: '#f59e0b', blue: '#3b82f6', purple: '#8b5cf6',
  text: '#E2EDE6', muted: '#7A9A80', border: 'rgba(255,255,255,0.09)', bg: '#0E1512',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function toLocalDateString(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function toDate(ts: any): Date | null {
  if (!ts) return null;
  if (ts && typeof ts === 'object' && 'toDate' in ts) return ts.toDate();
  const d = new Date(typeof ts === 'number' ? ts : String(ts));
  return isNaN(d.getTime()) ? null : d;
}

function fmtTime(ts: any): string {
  const d = toDate(ts);
  return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
}

function fmtDate(ts: any): string {
  const d = toDate(ts);
  return d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';
}

function fmtAmt(n: number) { return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }

function monthStart(d: Date): Date {
  const r = new Date(d); r.setDate(1); r.setHours(0, 0, 0, 0); return r;
}
function monthEnd(d: Date): Date {
  const r = new Date(d.getFullYear(), d.getMonth() + 1, 0); r.setHours(23, 59, 59, 999); return r;
}
function weekStart(d: Date): Date {
  const r = new Date(d); r.setDate(d.getDate() - d.getDay()); r.setHours(0, 0, 0, 0); return r;
}
function dayStart(d: Date): Date { const r = new Date(d); r.setHours(0,0,0,0); return r; }
function dayEnd(d: Date): Date { const r = new Date(d); r.setHours(23,59,59,999); return r; }

function daysBetween(a: Date, b: Date): number {
  return Math.round(Math.abs(b.getTime() - a.getTime()) / 86400000) + 1;
}

function isNightOrder(ts: any): boolean {
  const d = toDate(ts);
  if (!d) return false;
  const h = d.getHours(), m = d.getMinutes();
  return (h === 21 && m >= 30) || h === 22 || h === 23 || h === 0 || h === 1;
}

const STATUSES = ['DELIVERED','CANCELLED','CANCEL','PENDING','CONFIRMED','PROCESSING','ACCEPTED','PICKED','PREPARING'];
const STATUS_COLORS: Record<string,string> = {
  DELIVERED:'#21C98E', CANCELLED:'#ef4444', CANCEL:'#ef4444', PENDING:'#f59e0b',
  CONFIRMED:'#3b82f6', PROCESSING:'#21808D', ACCEPTED:'#21808D',
  PICKED:'#8b5cf6', PREPARING:'#8b5cf6',
};

// ── Main ──────────────────────────────────────────────────────────────────────
const DailyDashboard: React.FC<DailyDashboardProps> = ({ branchId }) => {
  const { width } = useWindowDimensions();
  const isMobile = width < 700;
  const today = new Date();

  const [startDate, setStartDate] = useState<Date>(() => monthStart(today));
  const [endDate, setEndDate] = useState<Date>(today);
  const [orders, setOrders] = useState<OrderModel[]>([]);
  const [dpMap, setDpMap] = useState<DPMap>({});
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const isLive = endDate >= dayStart(today);
  const isMultiDay = daysBetween(startDate, endDate) > 1;

  // ── Presets ───────────────────────────────────────────────────────
  const applyPreset = (preset: 'today' | 'week' | 'month' | 'lastmonth') => {
    const now = new Date();
    if (preset === 'today') { setStartDate(dayStart(now)); setEndDate(dayEnd(now)); }
    else if (preset === 'week') { setStartDate(weekStart(now)); setEndDate(dayEnd(now)); }
    else if (preset === 'month') { setStartDate(monthStart(now)); setEndDate(dayEnd(now)); }
    else {
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      setStartDate(monthStart(lm)); setEndDate(monthEnd(lm));
    }
  };

  const activePreset = useMemo(() => {
    const s = toLocalDateString(startDate), e = toLocalDateString(endDate);
    const t = toLocalDateString(today);
    if (s === t && e === t) return 'today';
    if (s === toLocalDateString(weekStart(today)) && e === t) return 'week';
    if (s === toLocalDateString(monthStart(today)) && e === t) return 'month';
    return 'custom';
  }, [startDate, endDate]); // eslint-disable-line

  // ── Firestore: orders ─────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    const start = dayStart(startDate), end = dayEnd(endDate);
    const q = query(
      collection(db, 'zapee_restaurant_orders'),
      where('createdAt', '>=', Timestamp.fromDate(start)),
      where('createdAt', '<=', Timestamp.fromDate(end)),
      orderBy('createdAt', 'desc'),
    );
    const unsub = onSnapshot(q, snap => {
      setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() } as OrderModel)));
      setLastUpdated(new Date()); setLoading(false);
    }, err => { console.error('Dashboard orders:', err); setLoading(false); });
    return () => unsub();
  }, [startDate, endDate]);

  // ── Firestore: DPs ────────────────────────────────────────────────
  useEffect(() => {
    getDocs(collection(db, 'zapee_delivery_partners')).then(snap => {
      const map: DPMap = {};
      snap.docs.forEach(d => {
        const data = d.data();
        const label = data.name || data.phone || d.id;
        [data.partnerId, data.uid, data.storeuid, data.pincode, d.id].forEach((k: any) => {
          if (k) map[String(k)] = label;
        });
      });
      setDpMap(map);
    });
  }, []);

  // ── Firestore: recent users ───────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, 'users'), orderBy('createdTimestamp', 'desc'), limit(15));
    const unsub = onSnapshot(q, snap => setRecentUsers(snap.docs.map(d => ({ id: d.id, ...d.data() } as RecentUser))));
    return () => unsub();
  }, []);

  // ── Stats ─────────────────────────────────────────────────────────
  const delivered  = useMemo(() => orders.filter(o => o.status === 'DELIVERED'), [orders]);
  const cancelled  = useMemo(() => orders.filter(o => ['CANCELLED','CANCEL'].includes(o.status || '')), [orders]);
  const pending    = useMemo(() => orders.filter(o => o.status === 'PENDING'), [orders]);
  const inProgress = useMemo(() => orders.filter(o => ['CONFIRMED','PROCESSING','ACCEPTED','PICKED','PREPARING'].includes(o.status || '')), [orders]);
  const assigned   = useMemo(() => orders.filter(o => !!o.deliveryPartnerId), [orders]);
  const unassigned = useMemo(() => orders.filter(o => !o.deliveryPartnerId && o.status !== 'DELIVERED' && !['CANCELLED','CANCEL'].includes(o.status||'')), [orders]);

  const revenue = useMemo(() => {
    // Product amount = sum of order items (qty × unit price), NOT totalPrice
    function itemTotal(o: OrderModel): number {
      return ((o as any).orderItems || []).reduce((s: number, it: any) => {
        const qty = Number(it.quantity ?? it.count ?? 1) || 0;
        const pr  = Number(it.unitPrice ?? it.price ?? 0) || 0;
        return s + qty * pr;
      }, 0);
    }
    function promo(o: OrderModel): any { return (o as any).promo || {}; }
    const prodAmt           = delivered.reduce((s, o) => s + itemTotal(o), 0);
    const delCharge         = delivered.reduce((s, o) => s + ((o.charges as any)?.deliveryCharge || 0), 0);
    const packing           = delivered.reduce((s, o) => s + ((o.charges as any)?.packing || 0), 0);
    const taxAmt            = delivered.reduce((s, o) => s + ((o.charges as any)?.tax || 0), 0);
    const platformFee       = delivered.reduce((s, o) => s + ((o.charges as any)?.platformFee || (o.charges as any)?.service || 0), 0);
    const surge             = delivered.reduce((s, o) => s + ((o.charges as any)?.surge || 0), 0);
    // Grand Total = exact sum of all charge components (not totalPrice, which may differ)
    const grandTotal        = prodAmt + delCharge + packing + taxAmt + platformFee + surge;
    const freeDeliveryCount = delivered.filter(o => promo(o).freeDelivery === true).length;
    const freeDeliveryAmt   = delivered.reduce((s, o) => s + (promo(o).freeDelivery ? Number(promo(o).freeDeliveryAmount || 0) : 0), 0);
    const offerDiscount     = delivered.reduce((s, o) => s + Number(promo(o).discountAmount || (o as any).couponDiscount || (o as any).discount || 0), 0);
    const offerCount        = delivered.filter(o => promo(o).discountAmount > 0 || (o as any).couponCode || (o as any).offerApplied).length;
    const nightOrderCount   = delivered.filter(o => isNightOrder(o.createdAt)).length;
    const nightBonus        = nightOrderCount * 50;
    const restaurantSettlement = prodAmt * 0.8;
    const dpSettlement      = delCharge + surge + nightBonus;
    const netProfit         = grandTotal - freeDeliveryAmt - offerDiscount - dpSettlement - restaurantSettlement;
    return { prodAmt, grandTotal, delCharge, packing, taxAmt, platformFee, surge, freeDeliveryAmt, freeDeliveryCount, offerDiscount, offerCount, nightOrderCount, nightBonus, restaurantSettlement, dpSettlement, netProfit, total: grandTotal, avg: delivered.length ? grandTotal / delivered.length : 0 };
  }, [delivered, orders]);

  // ── Daily trend (group orders by date) ───────────────────────────
  const dailyTrend = useMemo(() => {
    const map: Record<string, { orders: number; revenue: number; delivered: number }> = {};
    orders.forEach(o => {
      const d = toDate(o.createdAt);
      if (!d) return;
      const key = toLocalDateString(d);
      if (!map[key]) map[key] = { orders: 0, revenue: 0, delivered: 0 };
      map[key].orders++;
      if (o.status === 'DELIVERED') { map[key].revenue += (o as any).totalPrice || 0; map[key].delivered++; }
    });
    return Object.entries(map).sort(([a],[b]) => a.localeCompare(b));
  }, [orders]);

  const maxOrders = useMemo(() => Math.max(1, ...dailyTrend.map(([,v]) => v.orders)), [dailyTrend]);

  // ── DP performance ────────────────────────────────────────────────
  const dpPerformance = useMemo(() => {
    const map: Record<string, { name: string; delivered: number; total: number }> = {};
    orders.forEach(o => {
      if (!o.deliveryPartnerId) return;
      const name = dpMap[o.deliveryPartnerId] || o.deliveryPartnerId;
      if (!map[o.deliveryPartnerId]) map[o.deliveryPartnerId] = { name, delivered: 0, total: 0 };
      map[o.deliveryPartnerId].total++;
      if (o.status === 'DELIVERED') map[o.deliveryPartnerId].delivered++;
    });
    return Object.values(map).sort((a,b) => b.delivered - a.delivered).slice(0, 10);
  }, [orders, dpMap]);

  // ── Restaurant settlement breakdown ──────────────────────────────
  const restaurantSettleMap = useMemo(() => {
    const map: Record<string, { name: string; orders: number; prodAmt: number; settlement: number }> = {};
    delivered.forEach(o => {
      const key = (o as any).sellerId || 'unknown';
      const name = (o as any).sellerLocation?.name || key;
      if (!map[key]) map[key] = { name, orders: 0, prodAmt: 0, settlement: 0 };
      const pa = ((o as any).orderItems || []).reduce((s: number, it: any) => {
        return s + (Number(it.quantity ?? it.count ?? 1) || 0) * (Number(it.unitPrice ?? it.price ?? 0) || 0);
      }, 0);
      map[key].orders++;
      map[key].prodAmt += pa;
      map[key].settlement += pa * 0.8;
    });
    return Object.values(map).sort((a, b) => b.settlement - a.settlement);
  }, [delivered]);

  // ── DP settlement breakdown ───────────────────────────────────────
  const dpSettleMap = useMemo(() => {
    const map: Record<string, { name: string; orders: number; delCharge: number; surge: number; nightCount: number; nightBonus: number; total: number }> = {};
    delivered.forEach(o => {
      if (!o.deliveryPartnerId) return;
      const key = o.deliveryPartnerId;
      const name = dpMap[key] || key;
      if (!map[key]) map[key] = { name, orders: 0, delCharge: 0, surge: 0, nightCount: 0, nightBonus: 0, total: 0 };
      const dc = (o.charges as any)?.deliveryCharge || 0;
      const sg = (o.charges as any)?.surge || 0;
      const night = isNightOrder(o.createdAt) ? 1 : 0;
      map[key].orders++;
      map[key].delCharge  += dc;
      map[key].surge      += sg;
      map[key].nightCount += night;
      map[key].nightBonus += night * 50;
      map[key].total      += dc + sg + night * 50;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [delivered, dpMap]);

  // ── Range label ───────────────────────────────────────────────────
  const rangeLabel = useMemo(() => {
    const s = startDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    const e = endDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    if (toLocalDateString(startDate) === toLocalDateString(endDate)) return e;
    return `${s} – ${e}`;
  }, [startDate, endDate]);

  if (!branchId) return (
    <View style={styles.centered}><Text style={styles.emptyText}>No branch assigned.</Text></View>
  );

  return (
    <View style={styles.outer}>

      {/* ── Sticky date filter bar ── */}
      <View style={styles.header}>
        {/* Controls */}
        {/* Preset chips — left */}
        <View style={styles.presetRow}>
          {(['today','week','month','lastmonth'] as const).map(p => (
            <TouchableOpacity
              key={p}
              style={[styles.presetBtn, activePreset === p && styles.presetBtnActive]}
              onPress={() => applyPreset(p)}
            >
              <Text style={[styles.presetBtnText, activePreset === p && styles.presetBtnTextActive]}>
                {p === 'today' ? 'Today' : p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : 'Last Month'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Date range pickers — right */}
        <View style={styles.headerControls}>
          <View style={styles.dateRangeRow}>
            <input
              type="date"
              value={toLocalDateString(startDate)}
              max={toLocalDateString(endDate)}
              onChange={(e: any) => { if (e.target.value) setStartDate(new Date(e.target.value + 'T00:00:00')); }}
              style={dateInputStyle}
            />
            <Text style={styles.dateSep}>→</Text>
            <input
              type="date"
              value={toLocalDateString(endDate)}
              min={toLocalDateString(startDate)}
              max={toLocalDateString(new Date())}
              onChange={(e: any) => { if (e.target.value) setEndDate(new Date(e.target.value + 'T23:59:59')); }}
              style={dateInputStyle}
            />
            {isLive && (
              <View style={styles.liveBadge}>
                <View style={styles.liveDot} />
                <Text style={styles.liveText}>Live</Text>
              </View>
            )}
          </View>

          {loading && <ActivityIndicator size="small" color={C.primary} style={{ marginLeft: 8 }} />}
        </View>
      </View>

      <ScrollView style={styles.container} contentContainerStyle={styles.content}>

      {/* ── All 19 KPI cards — 6-column grid ── */}
      <View style={styles.kpiGrid}>
        {/* Order counts */}
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="receipt_long"    label="Total Orders"  value={orders.length}                    accent={C.primary} /></View>
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="check_circle"   label="Delivered"     value={delivered.length}                  accent={C.green}   pct={orders.length ? Math.round(delivered.length/orders.length*100) : 0} /></View>
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="cancel"         label="Cancelled"     value={cancelled.length}                  accent={C.red}     pct={orders.length ? Math.round(cancelled.length/orders.length*100) : 0} /></View>
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="pending_actions" label="Pending"       value={pending.length}                   accent={C.amber}   /></View>
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="local_shipping" label="In Progress"   value={inProgress.length}                 accent={C.blue}    /></View>
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="assignment_ind" label="Unassigned"    value={unassigned.length}                 accent={C.red}     /></View>
        {/* Revenue */}
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="currency_rupee" label="Grand Total"   value={fmtAmt(revenue.grandTotal)}        accent={C.green}   sub={`prod+del+pack+tax+platform+surge · ${delivered.length} del`} /></View>
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="storefront"     label="Product Sales" value={fmtAmt(revenue.prodAmt)}           accent={C.primary} sub="items × unit price" /></View>
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="two_wheeler"    label="Del. Charges"  value={fmtAmt(revenue.delCharge)}         accent={C.purple}  /></View>
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="inventory"      label="Packing"       value={fmtAmt(revenue.packing)}           accent={C.amber}   /></View>
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="bolt"           label="Surge"         value={fmtAmt(revenue.surge)}             accent="#f97316"   /></View>
        {/* Deductions */}
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="toll"           label="Platform Fee"  value={fmtAmt(revenue.platformFee)}       accent={C.primary} /></View>
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="local_shipping" label="Free Delivery" value={fmtAmt(revenue.freeDeliveryAmt)}  accent={C.teal}    sub={`${revenue.freeDeliveryCount} orders`} /></View>
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="local_offer"    label="Offer Applied" value={revenue.offerCount}                accent={C.purple}  sub={`${fmtAmt(revenue.offerDiscount)} saved`} /></View>
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="nightlight_round" label="Night Orders" value={revenue.nightOrderCount}          accent="#6366f1"   sub={`₹50×${revenue.nightOrderCount} = ${fmtAmt(revenue.nightBonus)}`} /></View>
        {/* Settlements */}
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="two_wheeler"    label="DP Settlement" value={fmtAmt(revenue.dpSettlement)}      accent={C.blue}    sub="del+surge+night bonus" /></View>
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="account_balance" label="Rest. Settlement" value={fmtAmt(revenue.restaurantSettlement)} accent={C.teal} sub="prod × 80%" /></View>
        <View style={[styles.kpiGridItem, isMobile && styles.kpiGridItemMobile]}><KpiCard icon="trending_up"    label="Net Profit"    value={fmtAmt(revenue.netProfit)}         accent={C.green}   sub="total−free−offers−DP−rest" /></View>
      </View>

      {/* ── Daily Trend Chart (multi-day) ── */}
      {isMultiDay && dailyTrend.length > 0 && (
        <View style={styles.section}>
          <View style={styles.sectionHead}>
            <Text style={styles.sectionTitle}>Daily Trend</Text>
            <Text style={styles.sectionSub}>{dailyTrend.length} days · orders &amp; revenue</Text>
          </View>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View style={styles.chartWrap}>
              {dailyTrend.map(([day, v]) => {
                const barH = Math.max(6, Math.round((v.orders / maxOrders) * 120));
                const d = new Date(day + 'T00:00:00');
                return (
                  <View key={day} style={styles.chartCol}>
                    <Text style={styles.chartVal}>{v.orders}</Text>
                    <View style={[styles.chartBar, { height: barH }]}>
                      <View style={[styles.chartBarFill, { height: Math.round((v.delivered / Math.max(1,v.orders)) * barH), backgroundColor: C.green }]} />
                    </View>
                    <Text style={styles.chartDay}>{d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</Text>
                    <Text style={styles.chartRev}>₹{(v.revenue/1000).toFixed(0)}k</Text>
                  </View>
                );
              })}
            </View>
          </ScrollView>
          <View style={styles.chartLegend}>
            <View style={styles.legendItem}><View style={[styles.legendDot, { backgroundColor: C.primary }]} /><Text style={styles.legendText}>Total orders</Text></View>
            <View style={styles.legendItem}><View style={[styles.legendDot, { backgroundColor: C.green }]} /><Text style={styles.legendText}>Delivered (inner)</Text></View>
          </View>
        </View>
      )}

      {/* ── Status Distribution ── */}
      {orders.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Status Distribution</Text>
          <View style={styles.distRow}>
            {[
              { label: 'Delivered', count: delivered.length, color: C.green },
              { label: 'In Progress', count: inProgress.length, color: C.blue },
              { label: 'Pending', count: pending.length, color: C.amber },
              { label: 'Cancelled', count: cancelled.length, color: C.red },
              { label: 'Unassigned', count: unassigned.length, color: C.purple },
            ].map(s => (
              <View key={s.label} style={styles.distItem}>
                <View style={styles.distLabelRow}>
                  <Text style={styles.distLabel}>{s.label}</Text>
                  <Text style={[styles.distCount, { color: s.color }]}>{s.count}</Text>
                </View>
                <View style={styles.distTrack}>
                  <View style={[styles.distFill, { width: `${orders.length ? (s.count/orders.length)*100 : 0}%` as any, backgroundColor: s.color }]} />
                </View>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* ── Settlement Maps Row ── */}
      <View style={[styles.tablesRow, isMobile && styles.tablesRowMobile]}>

        {/* Restaurant Settlement */}
        <View style={[styles.section, styles.tablePanel]}>
          <View style={styles.sectionHead}>
            <Text style={styles.sectionTitle}>Restaurant Settlement</Text>
            <Text style={styles.sectionSub}>{restaurantSettleMap.length} restaurants · prod × 80%</Text>
          </View>
          {restaurantSettleMap.length === 0 ? (
            <View style={styles.emptyBox}><Text style={styles.emptyText}>No delivered orders yet.</Text></View>
          ) : (
            <>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={[styles.cell, styles.cellPartner, styles.headerText]}>Restaurant</Text>
                <Text style={[styles.cell, styles.cellAmt,     styles.headerText]}>Orders</Text>
                <Text style={[styles.cell, styles.cellAmt,     styles.headerText]}>Prod Amt</Text>
                <Text style={[styles.cell, styles.cellAmt,     styles.headerText, { color: C.teal }]}>Settlement</Text>
              </View>
              {restaurantSettleMap.map((r, i) => (
                <View key={i} style={styles.tableRow}>
                  <Text style={[styles.cell, styles.cellPartner]} numberOfLines={1}>{r.name}</Text>
                  <Text style={[styles.cell, styles.cellAmt]}>{r.orders}</Text>
                  <Text style={[styles.cell, styles.cellAmt]}>₹{r.prodAmt.toFixed(0)}</Text>
                  <Text style={[styles.cell, styles.cellAmt, { color: C.teal, fontWeight: '700' }]}>₹{r.settlement.toFixed(0)}</Text>
                </View>
              ))}
              <View style={[styles.tableRow, styles.settleTotalRow]}>
                <Text style={[styles.cell, styles.cellPartner, styles.settleTotalLabel]}>Total</Text>
                <Text style={[styles.cell, styles.cellAmt, styles.settleTotalLabel]}>{restaurantSettleMap.reduce((s,r)=>s+r.orders,0)}</Text>
                <Text style={[styles.cell, styles.cellAmt, styles.settleTotalLabel]}>₹{restaurantSettleMap.reduce((s,r)=>s+r.prodAmt,0).toFixed(0)}</Text>
                <Text style={[styles.cell, styles.cellAmt, styles.settleTotalLabel, { color: C.teal }]}>₹{restaurantSettleMap.reduce((s,r)=>s+r.settlement,0).toFixed(0)}</Text>
              </View>
            </>
          )}
        </View>

        {/* DP Settlement */}
        <View style={[styles.section, styles.tablePanel]}>
          <View style={styles.sectionHead}>
            <Text style={styles.sectionTitle}>DP Settlement</Text>
            <Text style={styles.sectionSub}>{dpSettleMap.length} partners · del + surge + night bonus</Text>
          </View>
          {dpSettleMap.length === 0 ? (
            <View style={styles.emptyBox}><Text style={styles.emptyText}>No delivered orders yet.</Text></View>
          ) : (
            <>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={[styles.cell, styles.cellPartner, styles.headerText]}>Partner</Text>
                <Text style={[styles.cell, styles.cellAmt,     styles.headerText]}>Del Fee</Text>
                <Text style={[styles.cell, styles.cellAmt,     styles.headerText]}>Surge</Text>
                <Text style={[styles.cell, styles.cellAmt,     styles.headerText]}>Night</Text>
                <Text style={[styles.cell, styles.cellAmt,     styles.headerText, { color: C.green }]}>Total</Text>
              </View>
              {dpSettleMap.map((dp, i) => (
                <View key={i} style={styles.tableRow}>
                  <Text style={[styles.cell, styles.cellPartner]} numberOfLines={1}>{dp.name}</Text>
                  <Text style={[styles.cell, styles.cellAmt]}>₹{dp.delCharge.toFixed(0)}</Text>
                  <Text style={[styles.cell, styles.cellAmt]}>₹{dp.surge.toFixed(0)}</Text>
                  <View style={[styles.cell, styles.cellAmt]}>
                    {dp.nightCount > 0
                      ? <Text style={{ fontSize: 11, color: '#6366f1', fontWeight: '700' }}>₹{dp.nightBonus} <Text style={{ fontWeight: '400', color: C.muted }}>({dp.nightCount})</Text></Text>
                      : <Text style={{ fontSize: 11, color: C.muted }}>—</Text>
                    }
                  </View>
                  <Text style={[styles.cell, styles.cellAmt, { color: C.green, fontWeight: '700' }]}>₹{dp.total.toFixed(0)}</Text>
                </View>
              ))}
              <View style={[styles.tableRow, styles.settleTotalRow]}>
                <Text style={[styles.cell, styles.cellPartner, styles.settleTotalLabel]}>Total</Text>
                <Text style={[styles.cell, styles.cellAmt, styles.settleTotalLabel]}>₹{dpSettleMap.reduce((s,d)=>s+d.delCharge,0).toFixed(0)}</Text>
                <Text style={[styles.cell, styles.cellAmt, styles.settleTotalLabel]}>₹{dpSettleMap.reduce((s,d)=>s+d.surge,0).toFixed(0)}</Text>
                <Text style={[styles.cell, styles.cellAmt, styles.settleTotalLabel]}>₹{dpSettleMap.reduce((s,d)=>s+d.nightBonus,0).toFixed(0)}</Text>
                <Text style={[styles.cell, styles.cellAmt, styles.settleTotalLabel, { color: C.green }]}>₹{dpSettleMap.reduce((s,d)=>s+d.total,0).toFixed(0)}</Text>
              </View>
            </>
          )}
        </View>
      </View>

      {/* ── DP Performance ── */}
      <View style={styles.section}>
        <View style={styles.sectionHead}>
          <Text style={styles.sectionTitle}>Delivery Partner Performance</Text>
          <Text style={styles.sectionSub}>by delivered orders</Text>
        </View>
        {dpPerformance.length === 0 ? (
          <View style={styles.emptyBox}><Text style={styles.emptyText}>No assignments in this range.</Text></View>
        ) : (
          <>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.cell, styles.cellPartner, styles.headerText]}>Partner</Text>
              <Text style={[styles.cell, styles.cellAmt,     styles.headerText]}>Delivered</Text>
              <Text style={[styles.cell, styles.cellAmt,     styles.headerText]}>Total</Text>
              <Text style={[styles.cell, styles.cellStatus,  styles.headerText]}>Rate</Text>
            </View>
            {dpPerformance.map((dp, i) => (
              <View key={i} style={styles.tableRow}>
                <Text style={[styles.cell, styles.cellPartner]} numberOfLines={1}>{dp.name}</Text>
                <Text style={[styles.cell, styles.cellAmt, { color: C.green, fontWeight: '700' }]}>{dp.delivered}</Text>
                <Text style={[styles.cell, styles.cellAmt]}>{dp.total}</Text>
                <View style={[styles.cell, styles.cellStatus]}>
                  <View style={[styles.rateBadge, { backgroundColor: dp.total ? (dp.delivered/dp.total > 0.7 ? C.green : C.amber) + '20' : '#f1f5f9' }]}>
                    <Text style={[styles.rateText, { color: dp.total ? (dp.delivered/dp.total > 0.7 ? C.green : C.amber) : C.muted }]}>
                      {dp.total ? Math.round(dp.delivered/dp.total*100) : 0}%
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </>
        )}
      </View>

      {/* ── Recent Users ── */}
      <View style={styles.section}>
        <View style={styles.sectionHead}>
          <Text style={styles.sectionTitle}>Recent Users</Text>
          <Text style={styles.sectionSub}>latest 15 signups</Text>
        </View>
        {recentUsers.length === 0 ? (
          <View style={styles.emptyBox}><Text style={styles.emptyText}>No users yet.</Text></View>
        ) : (
          <>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.cell, styles.cellUser,   styles.headerText]}>Name / Phone</Text>
              <Text style={[styles.cell, styles.cellWallet, styles.headerText]}>Wallet</Text>
              <Text style={[styles.cell, styles.cellJoined, styles.headerText]}>Joined</Text>
              <Text style={[styles.cell, styles.cellStatus, styles.headerText]}>Status</Text>
            </View>
            {recentUsers.map(u => (
              <View key={u.id} style={styles.tableRow}>
                <View style={[styles.cell, styles.cellUser]}>
                  <Text style={styles.userName} numberOfLines={1}>{u.username || '—'}</Text>
                  <Text style={styles.userPhone}>{u.phone || '—'}</Text>
                </View>
                <Text style={[styles.cell, styles.cellWallet]}>₹{(u.walletBalance || 0).toFixed(0)}</Text>
                <Text style={[styles.cell, styles.cellJoined]}>{fmtDate(u.createdTimestamp)}</Text>
                <View style={[styles.cell, styles.cellStatus]}>
                  <View style={[styles.badge, { backgroundColor: u.notactive ? '#fee2e220' : '#d1fae520' }]}>
                    <Text style={[styles.badgeText, { color: u.notactive ? C.red : C.green }]}>
                      {u.notactive ? 'Inactive' : 'Active'}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </>
        )}
      </View>

      {/* Last updated */}
      <Text style={styles.lastUpdated}>Last updated: {lastUpdated.toLocaleTimeString()}</Text>

      </ScrollView>
    </View>
  );
};

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, accent, pct, big, sub }: {
  icon: string; label: string; value: string | number; accent: string; pct?: number; big?: boolean; sub?: string;
}) {
  return (
    <View style={[styles.kpiCard, big && styles.kpiCardBig, { borderTopColor: accent }]}>
      <View style={styles.kpiTop}>
        <span className="material-icons-round" style={{ fontSize: 18, color: accent, lineHeight: 1 }}>{icon}</span>
        {pct !== undefined && (
          <Text style={[styles.kpiPct, { color: accent }]}>{pct}%</Text>
        )}
      </View>
      <Text style={[styles.kpiValue, { color: accent }, big && styles.kpiValueBig]}>{value}</Text>
      <Text style={styles.kpiLabel}>{label}</Text>
      {sub !== undefined && <Text style={styles.kpiSub}>{sub}</Text>}
    </View>
  );
}

// ── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status.toUpperCase()] ?? '#94a3b8';
  return (
    <View style={[styles.badge, { backgroundColor: color + '20' }]}>
      <Text style={[styles.badgeText, { color }]}>{status}</Text>
    </View>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const dateInputStyle: React.CSSProperties = {
  height: 30, padding: '0 8px', borderRadius: 6,
  border: '1.5px solid rgba(33,128,141,0.3)', backgroundColor: 'rgba(255,255,255,0.08)',
  fontSize: 12, color: '#e2e8f0', outline: 'none', cursor: 'pointer',
  colorScheme: 'dark',
};

const styles = StyleSheet.create({
  outer: { flex: 1, flexDirection: 'column', backgroundColor: C.bg },
  container: { flex: 1, backgroundColor: C.bg },
  content: { padding: 20, paddingBottom: 40 },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 40 },

  // Header / Toolbar
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, backgroundColor: '#0A1712', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: 'rgba(33,128,141,0.18)' },
  tbLeft: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  tbTitle: { fontSize: 15, fontWeight: '800', color: '#fff' },
  tbSub: { fontSize: 11, color: '#8BAAB8', marginTop: 1 },
  headerControls: { flexDirection: 'column', gap: 8, alignItems: 'flex-end' },

  presetRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  presetBtn: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.08)', borderWidth: 1, borderColor: 'rgba(33,128,141,0.3)' },
  presetBtnActive: { backgroundColor: C.primary, borderColor: C.primary },
  presetBtnText: { fontSize: 12, fontWeight: '600', color: '#8BAAB8' },
  presetBtnTextActive: { color: '#fff' },

  dateRangeRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  dateSep: { fontSize: 16, color: '#8BAAB8', fontWeight: '600' },
  liveBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(33,201,142,0.12)', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 20, borderWidth: 1, borderColor: 'rgba(33,201,142,0.3)' },
  liveDot: { width: 7, height: 7, borderRadius: 4, backgroundColor: C.green },
  liveText: { fontSize: 11, fontWeight: '700', color: C.green },

  // KPI
  kpiRow: { flexDirection: 'row', gap: 10, marginBottom: 14, flexWrap: 'wrap' },
  kpiRowMobile: { flexDirection: 'row', flexWrap: 'wrap' },
  kpiCard: { flex: 1, minWidth: 110, backgroundColor: '#17201B', borderRadius: 12, padding: 14, borderTopWidth: 3, borderWidth: 1, borderColor: C.border, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 4, elevation: 1 },
  kpiCardBig: { flex: 1.4 },

  // 6-column grid
  kpiGrid: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -5, marginBottom: 14 },
  kpiGridItem: { width: '16.666%' as any, padding: 5 },
  kpiGridItemMobile: { width: '50%' as any },

  kpiTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  kpiValue: { fontSize: 22, fontWeight: '800', marginBottom: 2 },
  kpiValueBig: { fontSize: 26 },
  kpiLabel: { fontSize: 11, color: C.muted, fontWeight: '600' },
  kpiSub: { fontSize: 10, color: C.muted, marginTop: 2 },
  kpiPct: { fontSize: 11, fontWeight: '700' },

  // Chart
  section: { backgroundColor: '#17201B', borderRadius: 14, padding: 16, marginBottom: 14, borderWidth: 1, borderColor: C.border },
  sectionHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  sectionTitle: { fontSize: 14, fontWeight: '800', color: C.text },
  sectionSub: { fontSize: 11, color: C.muted },

  chartWrap: { flexDirection: 'row', alignItems: 'flex-end', gap: 10, paddingBottom: 4 },
  chartCol: { alignItems: 'center', minWidth: 40 },
  chartVal: { fontSize: 10, fontWeight: '700', color: C.text, marginBottom: 3 },
  chartBar: { width: 28, backgroundColor: C.primary + '30', borderRadius: 4, justifyContent: 'flex-end', overflow: 'hidden' },
  chartBarFill: { width: '100%', borderRadius: 4 },
  chartDay: { fontSize: 9, color: C.muted, marginTop: 4, textAlign: 'center' },
  chartRev: { fontSize: 9, color: C.primary, fontWeight: '700', textAlign: 'center' },
  chartLegend: { flexDirection: 'row', gap: 16, marginTop: 10 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  legendDot: { width: 8, height: 8, borderRadius: 4 },
  legendText: { fontSize: 11, color: C.muted },

  // Status distribution
  distRow: { gap: 10 },
  distItem: { marginBottom: 6 },
  distLabelRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  distLabel: { fontSize: 12, color: C.text, fontWeight: '600' },
  distCount: { fontSize: 12, fontWeight: '800' },
  distTrack: { height: 6, backgroundColor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' },
  distFill: { height: 6, borderRadius: 3 },

  // Tables
  tablesRow: { flexDirection: 'row', gap: 14, marginBottom: 14 },
  tablesRowMobile: { flexDirection: 'column' },
  tablePanel: { flex: 1 },
  tableRow: { flexDirection: 'row', paddingVertical: 7, borderBottomWidth: 1, borderBottomColor: '#f1f5f9', alignItems: 'center' },
  tableHeader: { backgroundColor: '#f8fafc', borderRadius: 8, marginBottom: 2 },
  settleTotalRow: { backgroundColor: '#f0fdf4', borderRadius: 8, marginTop: 4, borderBottomWidth: 0 },
  settleTotalLabel: { fontSize: 12, fontWeight: '800', color: C.text },
  headerText: { fontSize: 10, fontWeight: '700', color: C.muted, textTransform: 'uppercase' },
  cell: { flex: 1, fontSize: 12, color: C.text },
  cellOrder:  { flex: 1.4 },
  cellTime:   { flex: 1 },
  cellAmt:    { flex: 1 },
  cellStatus: { flex: 1.3 },
  cellPartner:{ flex: 1.5 },
  cellUser:   { flex: 2 },
  cellWallet: { flex: 1 },
  cellJoined: { flex: 1.2 },

  badge: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 20, alignSelf: 'flex-start' },
  badgeText: { fontSize: 10, fontWeight: '700' },
  rateBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 20, alignSelf: 'flex-start' },
  rateText: { fontSize: 11, fontWeight: '800' },

  userName: { fontSize: 12, fontWeight: '600', color: C.text },
  userPhone: { fontSize: 11, color: C.muted },
  emptyBox: { padding: 24, alignItems: 'center' },
  emptyText: { fontSize: 13, color: C.muted },
  loadingText: { fontSize: 14, color: C.muted, marginTop: 10 },
  lastUpdated: { fontSize: 10, color: '#5A7A60', textAlign: 'center', marginTop: 8 },

  // keep legacy keys referenced nowhere (safe to omit; unused)
  muted: { color: C.muted },
});

export default DailyDashboard;
